import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Link as LinkIcon, Copy, Check, X, Info, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'

export default function ResourceLink() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading, showBackButton, hideBackButton } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  const [invitationLink, setInvitationLink] = useState<string>('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')
  const [copiedLink, setCopiedLink] = useState(false)

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      setInvitationLink(resource.invitation_link || '')
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copiedLink) {
      const timer = setTimeout(() => {
        setCopiedLink(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [copiedLink])

  const handleCopyLink = async () => {
    if (!invitationLink) return

    try {
      await navigator.clipboard.writeText(invitationLink)
      setCopiedLink(true)
      hapticFeedback('light')
    } catch (error) {
      console.error('Failed to copy link:', error)
    }
  }

  const validateLink = (link: string): boolean => {
    if (!link.trim()) {
      setError('Ссылка не может быть пустой')
      return false
    }

    const linkRegex = /^(https:\/\/(t|telegram)\.me\/|@)/
    if (!linkRegex.test(link)) {
      setError('Ссылка должна начинаться с https://t.me/, https://telegram.me/ или @')
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!resource) return

    if (!validateLink(invitationLink)) {
      hapticNotification('error')
      return
    }

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: {
          invitation_link: invitationLink.trim(),
        },
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      hideBackButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update invitation link:', error)
      setError('Ошибка при сохранении ссылки')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Сохранить ссылку', onSave, '#5288c1', '#ffffff')
    showBackButton(() => {
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`)
    })

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      hideBackButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, showBackButton, hideBackButton, setMainButtonLoading, navigate, resourceId])

  if (isLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <LinkIcon className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Ссылка на ресурс</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Link Input - Integration Style */}
        <div className="border-2 border-purple-500/30 rounded-xl bg-gradient-to-r from-purple-500/5 to-pink-500/5 overflow-hidden">
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-lg font-bold text-tg-text">Ссылка на ресурс</h2>
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full">
                    Telegram
                  </span>
                </div>
                <p className="text-xs text-tg-hint">
                  Пригласительная ссылка для вступления в канал/группу
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="relative">
                <input
                  type="text"
                  value={invitationLink}
                  onChange={(e) => {
                    setInvitationLink(e.target.value)
                    setError('')
                  }}
                  onFocus={(e) => {
                    hapticFeedback('light')
                    // Move cursor to the beginning
                    setTimeout(() => {
                      e.target.setSelectionRange(0, 0)
                      e.target.scrollLeft = 0
                    }, 0)
                  }}
                  style={{ touchAction: 'pan-x' }}
                  className="w-full px-4 py-3 pr-20 text-base border-2 border-purple-500/20 rounded-lg bg-tg-secondary-bg text-tg-text placeholder:text-tg-hint focus:border-purple-500 focus:outline-none transition-colors overflow-x-auto"
                  placeholder="https://t.me/joinchat/... или https://telegram.me/..."
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {invitationLink && (
                    <button
                      onClick={() => {
                        setInvitationLink('')
                        hapticFeedback('light')
                      }}
                      className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                      title="Очистить"
                    >
                      <X className="w-4 h-4 text-tg-hint" />
                    </button>
                  )}
                  <button
                    onClick={handleCopyLink}
                    className="p-1.5 hover:bg-purple-500/10 rounded-lg transition-colors"
                    title="Копировать ссылку"
                  >
                    <Copy className={`w-4 h-4 ${copiedLink ? 'text-green-500' : 'text-purple-400'}`} />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {resource.invitation_link && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <Check className="w-3 h-3" />
                      <span>Настроена</span>
                    </div>
                  )}
                  {copiedLink && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <Check className="w-3 h-3" />
                      <span>Скопировано</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">Форматы пригласительной ссылки</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Здесь будет подробное описание.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
